import { FC, useState } from "react";
import { Controller, SubmitHandler, useFormContext } from "react-hook-form";
import { Box, Paper, MenuItem, Select, useTheme } from "@mui/material";
import __ from "src/utils/Translations";
import useOrgchartStore from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureView/Utils/store";
import { useHistory, useParams } from "react-router-dom";
import useIsAhr from "src/utils/hooks/useIsAhr";
import EmployeeAutocomplete from "./Components/Autocomplete/EmployeeAutocomplete";
import AreaAutocomplete from "./Components/Autocomplete/AreaAutocomplete";
import {
  IOrgchartViewQueryParams,
  IFilterBar,
  OrgchartType,
} from "./FilterBar.models";

const FilterBar: FC<IFilterBar> = ({
  updateQueryParameters,
  methods,
  companyId,
}) => {
  const isAhr = useIsAhr();

  const theme = useTheme();
  const { setNodeToFocusOn } = useOrgchartStore.getState();
  const history = useHistory();
  const { orgchartType } = useParams() as { orgchartType: OrgchartType };
  const [autocompleteOrgchartType, setAutocompleteOrgchartType] =
    useState(orgchartType);
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = methods;
  const { resetField } = useFormContext();

  const onSubmit: SubmitHandler<IOrgchartViewQueryParams> = (data) => {
    const submitedOrgchartType = data.orgChartType;

    if (submitedOrgchartType !== autocompleteOrgchartType) {
      useOrgchartStore.setState(useOrgchartStore.getInitialState());
      resetField("autocomplete", {
        defaultValue: "",
      });
      setAutocompleteOrgchartType(submitedOrgchartType);
      history.push(
        `${
          isAhr ? "/ahr" : `/company/edit/${companyId}`
        }/organization-structure/configure-structure/structure-view/${submitedOrgchartType}`
      );
    }
    // eslint-disable-next-line
    setNodeToFocusOn(undefined);
    updateQueryParameters(data);
  };

  return (
    <Paper component="form" onSubmit={handleSubmit(onSubmit)} sx={{ p: 2 }} data-testid="os__view-filter-bar">
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          flexDirection: "column",
          gap: "16px",
          justifyContent: "flex-start",
          [theme.breakpoints.up("md")]: {
            flexDirection: "row",
            alignItems: "center",
          },
        }}
      >
        <Controller
          control={control}
          name="orgChartType"
          render={({ field }) => (
            <Select
              {...field}
              data-testid="os__view-type-select"
              sx={{
                width: "100%",
                [theme.breakpoints.up("md")]: {
                  width: "unset",
                  minWidth: "300px",
                },
              }}
              onChange={(event) => {
                resetField("autocomplete", {
                  defaultValue: "",
                });
                field.onChange(event);
                handleSubmit(onSubmit)();
              }}
              value={field.value}
              variant="outlined"
              error={!!errors.orgChartType}
            >
              <MenuItem value={`${OrgchartType.Areas}`}>
                {__("Struktura Organizacji")}
              </MenuItem>
              <MenuItem value={`${OrgchartType.Employees}`}>
                {__("Struktura Pracownik - Przełożony ")}
              </MenuItem>
            </Select>
          )}
        />
        {autocompleteOrgchartType === OrgchartType.Employees ? (
          <EmployeeAutocomplete
            name="autocomplete"
            label={__("Szukaj")}
            onSubmit={() => handleSubmit(onSubmit)()}
          />
        ) : (
          <AreaAutocomplete
            name="autocomplete"
            label={__("Szukaj")}
            onSubmit={() => handleSubmit(onSubmit)()}
          />
        )}
      </Box>
    </Paper>
  );
};

export default FilterBar;
